import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from './components/Layout';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import About from './pages/About';
import AIFamily from './pages/AIFamily';
import DigitalTalent from './pages/DigitalTalent';
import PersonalizedService from './pages/PersonalizedService';
import CorePeople from './pages/CorePeople';
import JoinFutureWorld from './pages/JoinFutureWorld';
import ProjectCooperation from './pages/ProjectCooperation';
import NewsCenter from './pages/resources/NewsCenter';
import ResourceCenter from './pages/resources/ResourceCenter';
import NewsArticle from './pages/resources/NewsArticle';
import ResourceLibraryArticle from './pages/resources/ResourceLibraryArticle';
import ContentConsolePage, { CONTENT_CONSOLE_ROUTE } from './pages/ops/ContentConsolePage';
import { usePageAnalytics } from './hooks/usePageAnalytics';
import useDocumentTitle from './hooks/useDocumentTitle';

type ConstructionPageCopyKey =
  | 'pageUnderConstruction'
  | 'featureInProgress'
  | 'hiringInProgress'
  | 'jobSeekingInProgress'
  | 'routeConfigured'
  | 'notFound';

const getConstructionCopy = (language: string) => {
  if (language === 'zh') {
    return {
      pageUnderConstruction: '页面建设中',
      featureInProgress: '功能正在制作中',
      hiringInProgress: '招聘功能建设中',
      jobSeekingInProgress: '求职功能建设中',
      routeConfigured: '路由已配置，内容正在完善中',
      notFound: '页面建设中 (404)',
      comingSoon: '敬请期待',
    };
  }

  if (language === 'ja') {
    return {
      pageUnderConstruction: 'ページ準備中',
      featureInProgress: '機能を準備中です',
      hiringInProgress: '採用機能を準備中です',
      jobSeekingInProgress: '求職機能を準備中です',
      routeConfigured: 'ルートは設定済みで、コンテンツを制作中です',
      notFound: 'ページ準備中 (404)',
      comingSoon: '公開まで今しばらくお待ちください',
    };
  }

  return {
    pageUnderConstruction: 'Page Under Construction',
    featureInProgress: 'Feature In Progress',
    hiringInProgress: 'Hiring Feature In Progress',
    jobSeekingInProgress: 'Job Seeking Feature In Progress',
    routeConfigured: 'The route is configured and the content is being prepared.',
    notFound: 'Page Under Construction (404)',
    comingSoon: 'Coming soon',
  };
};

const normalizeLanguage = (language?: string | null) => {
  const normalized = (language ?? '').toLowerCase();
  if (normalized.startsWith('zh')) return 'zh';
  if (normalized.startsWith('ja')) return 'ja';
  return 'en';
};

const ConstructionPage: React.FC<{ titleKey?: ConstructionPageCopyKey; descriptionKey?: 'comingSoon' | 'routeConfigured' }> = ({
  titleKey = 'pageUnderConstruction',
  descriptionKey = 'comingSoon',
}) => {
  const { i18n } = useTranslation();
  const locale = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const copy = getConstructionCopy(locale);
  const title = copy[titleKey];
  const description = copy[descriptionKey];

  useDocumentTitle(title);

  return (
    <div style={{ textAlign: 'center', padding: '150px 0' }}>
      <h2>{title}</h2>
      <p>{description}</p>
    </div>
  );
};

const App: React.FC = () => {
  usePageAnalytics();

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path={CONTENT_CONSOLE_ROUTE} element={<ContentConsolePage />} />
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="about" element={<About />} />
          <Route path="ai" element={<AIFamily />} />
          <Route path="ai/hiring" element={<ConstructionPage titleKey="hiringInProgress" />} />
          <Route path="ai/job-seeking" element={<ConstructionPage titleKey="jobSeekingInProgress" />} />
          <Route path="resources" element={<ResourceCenter />} />
          <Route path="resources/news" element={<NewsCenter />} />
          <Route path="resources/news/:slug" element={<NewsArticle />} />
          <Route path="resources/library/:slug" element={<ResourceLibraryArticle />} />
          <Route path="resources/core-people" element={<CorePeople />} />
          <Route path="services/china/enterprise/onestop" element={<PersonalizedService />} />
          <Route path="services/china/personal/digital" element={<DigitalTalent />} />
          <Route path="contact/projects" element={<ProjectCooperation />} />
          <Route path="contact/projects/:slug" element={<ProjectCooperation />} />
          <Route path="contact/join" element={<JoinFutureWorld />} />
          <Route path="contact/join/:slug" element={<JoinFutureWorld />} />
          <Route path="*" element={<ConstructionPage titleKey="notFound" descriptionKey="routeConfigured" />} />
        </Route>
      </Routes>
    </>
  );
};

export default App;
